import * as d3 from 'd3';
import { colors, DURATION } from '../utils';
import './d3-waterfallchart.css';

export default function () {
  const margin = { top: 10, right: 20, bottom: 70, left: 100 };

  let width = 400;
  let height = 300;
  let svg;
  let t;
  let _data;
  let chartWidth;
  let chartHeight;
  let x;
  let y;

  function chart(selection, isResize) {
    selection.each(function (data) {
      const partialSums = [];
      let sum = 0;

      data.forEach(function (d) {
        partialSums.push(sum);
        sum += d.value;
      });

      const duration = isResize ? 0 : DURATION;
      t = d3.transition().duration(duration);

      _data = data.map(function (d, i) { return { id: d.id, value: d.value, baseValue: partialSums[i] }; });

      chartWidth = width - margin.left - margin.right;
      chartHeight = height - margin.top - margin.bottom;

      createScales();

      if (!svg) {
        svg = selection.append('svg');
        const g = svg.append('g')
          .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

        createAxes();
        createLabel();

        g.append('g')
          .attr('class', 'waterfall-chart__bars')
          .append('path');
      }

      svg.attr('width', width).attr('height', height);

      drawAxes();
      drawLabel();
      drawBars();
    });
  }

  function createScales() {
    x = d3.scaleLinear().rangeRound([0, chartWidth]);
    y = d3.scaleBand().range([chartHeight, 0]).padding(0.1);

    const xMax = d3.max(_data.map(function (d) { return d.value + d.baseValue; }));

    x.domain([0, xMax]).nice();
    y.domain(_data.map(function (d) { return d.id; }));
  }

  function createAxes() {
    svg.select('g')
      .append('g')
      .attr('class', 'waterfall-chart__axis waterfall-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'waterfall-chart__axis waterfall-chart__axis--y')
  }

  function createLabel() {
    svg.append('text')
      .attr('class', 'waterfall-chart__label')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('(£M)');
  }

  function drawAxes() {
    svg.select('.waterfall-chart__axis--x')
      .attr('transform', 'translate(0,' + chartHeight + ')')
      .transition(t)
      .call(d3.axisBottom(x).tickFormat(function (d) { return d3.format(',.0f')(d / 1e6); }).tickSize(-chartHeight));

    svg.select('.waterfall-chart__axis--x')
      .selectAll('text')
      .attr('dx', '-0.8em')
      .attr('dy', '.35em')
      .attr('transform', 'rotate(-45)')
      .attr('text-anchor', 'end');

    svg.select('.waterfall-chart__axis--x')
      .selectAll('.tick line')
      .attr('stroke', '#CCC');

    const g = svg.select('.waterfall-chart__axis--y')
      .call(d3.axisLeft(y).tickSize(0));

    g.select('.domain').remove(); // Created by d3.axisLeft

    g.selectAll('.tick line')
      .attr('stroke', colors.lightgray);

    g.selectAll('.tick text')
      .attr('x', -6)
      .attr('dy', 4);
  }

  function drawLabel() {
    svg.select('.waterfall-chart__label')
      .attr('x', width / 2)
      .attr('y', height - 20);
  }

  function drawBars() {
    const MINIMUM_LABEL_WIDTH = 60;

    const bars = svg.select('.waterfall-chart__bars')
      .selectAll('.waterfall-chart__bar')
      .data(_data);

    const barsEnter = bars.enter()
      .append('g')
      .attr('class', 'waterfall-chart__bar');

    barsEnter.append('rect')
      .attr('class', 'waterfall-chart__bar--rect')
      .attr('x', function (d) { return x(d.baseValue + Math.min(0, d.value)); })
      .attr('y', function (d) { return y(d.id); })
      .attr('width', 0)
      .attr('height', y.bandwidth())
      .merge(bars.select('rect'))
      .transition(t)
      .attr('x', function (d) { return x(d.baseValue + Math.min(0, d.value)); })
      .attr('y', function (d) { return y(d.id); })
      .attr('width', function (d) { return Math.abs(x(Math.abs(d.value))); })
      .attr('height', y.bandwidth())
      .attr('fill', function (d, i) {
        if (i === _data.length - 1) {
          return 'steelblue';
        } else if (d.value >= 0) {
          return colors.pro;
        } else {
          return colors.con;
        }
      });

    barsEnter.append('text')
      .attr('class', 'row-chart__bar--label')
      .attr('x', function (d) { return x(d.baseValue + Math.max(0, d.value)); })
      .attr('y', function (d) { return y(d.id) + y.bandwidth() / 2; })
      .attr('dx', function (d) {
        return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? '-4px' : '4px';
      })
      .attr('text-anchor', function (d) {
        return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? 'end' : 'start';
      })
      .attr('alignment-baseline', 'middle')
      .attr('fill', function (d) { return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? 'white' : 'black'; })
      .attr('font-weight', 'bold')
      .text(function (d) { return d3.formatPrefix(',.0', 1e6)(d.value); })
      .merge(bars.select('text'))
      .transition(t)
      .attr('x', function (d) { return x(d.baseValue + Math.max(0, d.value)); })
      .attr('y', function (d) { return y(d.id) + y.bandwidth() / 2; })
      .attr('dx', function (d) {
        return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? '-4px' : '4px';
      })
      .attr('text-anchor', function (d) {
        return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? 'end' : 'start';
      })
      .attr('fill', function (d) { return x(Math.abs(d.value)) > MINIMUM_LABEL_WIDTH ? 'white' : 'black'; })
      .text(function (d) { return d3.formatPrefix(',.0', 1e6)(Math.abs(d.value)); });
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-waterfallchart.js